<template>
  <div class="search-module-detail-plan">
    <div class="wrapper">
      <div :class="moduleDetailActionsClass" class="module-detail-actions">
        <div class="module-detail-header">
          <module-plan-meta-data :module="module" />
        </div>
        <div class="toggle-wrapper">
          <cv-toggle
            small
            class="notes-toggle"
            value=""
            :label="t('module_plan_view_details_text')"
            :checked="toggleAccordionState"
            @change="handleAllAccordions"
          >
            <template #text-left>{{ emptyText }}</template>
            <template #text-right>{{ emptyText }}</template>
          </cv-toggle>
        </div>
      </div>
      <div class="module-detail-content">
        <!-- Introduction-->
        <div v-if="module.showCover" id="module-cover" class="introduction">
          <AccordionWrapper
            class="module-plan-accordion-component detail"
            @state-change="handleModuleAccordionToggle"
          >
            <cv-accordion-item :open="moduleToggle">
              <template #title>
                <match-indicator v-if="matchDescriptionOrNotes" />
                <div class="wrapper">
                  <div class="component-content">
                    <dl class="field-set title accordion-title">
                      <dd class="field-value">
                        <span class="icon">
                          <svg-icon :name="coverIcon" kind="indicative" />
                        </span>
                        <span class="text">{{ t("module_plan_introduction_text") }}</span>
                      </dd>
                    </dl>
                  </div>
                </div>
              </template>

              <template #content>
                <template v-if="isBlank">
                  <empty-placeholder>
                    <template #icon>
                      <AnnotationCardsEmpty />
                    </template>
                    {{ t("module_plan_no_notes_and_description") }}
                  </empty-placeholder>
                </template>
                <template v-else>
                  <div class="wrapper" @click.stop>
                    <template v-if="isTeacher">
                      <h6>{{ t("module_plan_notes") }}</h6>
                      <rich-text
                        :lazyload="false"
                        :content="notes"
                        :expandable="true"
                        :expanded="true"
                      />
                    </template>
                    <h6>{{ t("module_plan_description") }}</h6>
                    <rich-text
                      v-if="moduleToggle"
                      :lazyload="false"
                      :content="description"
                      :expandable="true"
                      :expanded="true"
                    />
                  </div>
                </template>
              </template>
            </cv-accordion-item>
          </AccordionWrapper>
        </div>

        <!-- Section with cover-->
        <div v-if="showSectionCover || isTeacher" class="section-detail-content">
          <div v-if="isMultipleSectionSelectionMode" class="entire-module-selection" @click.stop>
            <input-checkbox
              id="select-entire-module"
              :value="ENTIRE_MODULE_SELECTION_VALUE"
              :selected-values="entireModuleSelectionValues"
              @change="handleSelectEntireModule"
            >
              {{ t("search_module_detail_select_entire_module_label") }}
            </input-checkbox>
          </div>
          <cv-radio-group vertical="vertical" legend-text=" " hide-legend>
            <div
              v-for="section in sections"
              :key="section.id"
              class="section-grouper"
              :style="cssVars"
            >
              <module-plan-section-component
                :module="module"
                :section="section"
                :selected-sections="selectedSections"
                :is-accordion-open="sectionToggleState[section.id]"
                :hide-section-activity-number="showSectionCover ? hideSectionActivityNumber : true"
                :params="params"
                :selection-mode="selectionMode"
                @toggle-section-accordion="handleSectionAccordionToggle"
                @update-selected-section="updateSelectedSection"
              />
              <div class="section-info">
                <div v-if="sectionCompletionInfo(section) && totalStudent" class="section-progress">
                  <stacked-column-bar
                    :categories="sectionCompletionInfoValues(section)"
                    :max-value="totalStudent"
                    :show-header="false"
                    :show-value-count="false"
                  />
                </div>
                <div class="actions-buttons">
                  <div v-if="showPrerequisite(section)" class="section-prerequisite">
                    <course-plan-prerequisite-component
                      :section="section"
                      :section-prerequisite="[]"
                      :section-list="sections"
                      :sequential-section-completion-req="sequentialSectionCompletionReq"
                      :hide-section-activity-number="hideSectionActivityNumber"
                      :prerequisites-drop-down-data="prerequisitesDropDownData"
                      :existing-operators="[]"
                      :lesson-id="moduleId"
                    />
                  </div>
                  <cv-link
                    v-if="!isAdaptiveSection(section.appFeatureIds)"
                    @click="handleToggleExpand(section.id)"
                  >
                    {{ sectionToggleLabel(section) }}
                  </cv-link>
                </div>
              </div>
              <div v-if="isExpanded(section.id)" class="section-activities">
                <cv-skeleton-text
                  v-if="isLoading(section.id)"
                  paragraph
                  :line-count="3"
                  width="100%"
                />
                <template v-else>
                  <course-plan-activity-sequential-access-component
                    :course="module"
                    :section="section"
                  />
                  <div class="module-plan-activity-component-list">
                    <module-plan-activity-component
                      v-for="activity in section.activityData"
                      :key="activity.id"
                      :section="section"
                      :activity="activity"
                      :is-accordion-open="activitiesToggleState[activity.id]"
                      :hide-section-activity-number="hideSectionActivityNumber"
                      :params="params"
                      :selected-sections="selectedSections"
                      :selected-activities="selectedActivities"
                      :selection-mode="selectionMode"
                      @toggle-activity-accordion="handleActivityAccordionToggle"
                      @update-selected-activity="updateSelectedActivity"
                    />
                    <div
                      :id="sectionEndId(section.id)"
                      class="page-component component-editable section-end"
                      :class="{
                        disabled: isAlsLesson,
                      }"
                    >
                      <div class="wrapper">
                        <div class="component-content">
                          <dl class="field-set title">
                            <dd class="field-value">
                              <span class="icon">
                                <svg-icon
                                  :key="sectionIconType(section)"
                                  :name="sectionIconType(section)"
                                  kind="indicative"
                                />
                              </span>
                              <span class="text">{{
                                t("module_plan_section_completion_text")
                              }}</span>
                            </dd>
                          </dl>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </cv-radio-group>
        </div>

        <!-- Not viewable section -->
        <div
          v-if="!showSectionCover && hasActivities && !isTeacher"
          class="activities-detail-content"
        >
          <module-plan-activity-component
            v-for="activity in sections[0].activityData"
            :key="activity.id"
            class="activity"
            :section="sections[0]"
            :activity="activity"
            :is-accordion-open="activitiesToggleState[activity.id]"
            :hide-section-activity-number="hideSectionActivityNumber"
            :params="params"
            :selected-sections="selectedSections"
            :selected-activities="selectedActivities"
            :selection-mode="selectionMode"
            @toggle-activity-accordion="handleActivityAccordionToggle"
            @update-selected-activity="updateSelectedActivity"
          />
        </div>

        <div v-if="module.showCover" id="module-end" class="page-component module-end">
          <div class="wrapper">
            <div class="component-content">
              <dl class="field-set title">
                <dd class="field-value">
                  <span class="icon">
                    <svg-icon :key="moduleEndIcon" :name="moduleEndIcon" kind="indicative" />
                  </span>
                  <span class="text">{{ t("module_plan_module_end_text") }}</span>
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import {
  COMPLETION_STATUS,
  LESSON_ACTIVITY_INDICATOR_ICON,
  NAMESPACE,
} from "@modules/search/service/search.constant.ts";
import AnnotationCardsEmpty from "@images/placeholder/empty-states/AnnotationCardsEmpty.svg?component";
import { isEmpty, isNull } from "@utils/var.check.ts";
import DateTimeFormatter from "@utils/datetime.formatter.ts";
import DateTimeUtils from "@utils/datetime.utils.ts";
import coursePlanStore from "@plugins/course-lesson-plan/components/CoursePlanStore.ts";
import {
  INTEGRATOR_APP_FEATURE_CATEGORY,
  INTEGRATOR_APP_PROVIDER,
} from "@modules/app/service/app.constant.ts";
import CoursePlanActivitySequentialAccessComponent from "@plugins/course-lesson-plan/components/CoursePlanActivitySequentialAccessComponent.vue";
import CoursePlanPrerequisiteComponent from "@plugins/course-lesson-plan/components/CoursePlanPrerequisiteComponent.vue";
import ModulePlanMetaData from "@modules/search/components/ModulePlanMetaData.vue";
import ModulePlanSectionComponent from "@modules/search/components/ModulePlanSectionComponent.vue";
import ModulePlanActivityComponent from "@modules/search/components/ModulePlanActivityComponent.vue";
import { FAILED_REASON } from "@plugins/rule-engine/rule.constant";
import subjectColorMap from "@configs/subjectColorMap";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import EmptyPlaceholder from "@plugins/common/components/EmptyPlaceholder.vue";
import AccordionWrapper from "@plugins/common/components/AccordionWrapper.vue";
import RichText from "@plugins/richtext/components/RichText.vue";
import StackedColumnBar from "@plugins/common/components/StackedColumnBar.vue";
import InputCheckbox from "@plugins/common/components/InputCheckbox.vue";
import { AccordionActionData, StackedColumnBarCategory } from "@plugins/common/types.ts";
import {
  LessonSelectionModeEnum,
  LessonSelectionTypeEnum,
  SearchModuleDetail,
} from "@modules/resource-search/resource-search-types.ts";
import MatchIndicator from "@plugins/common/components/MatchIndicator.vue";

interface Props {
  params?: SearchModuleDetail;
  selectedSections?: unknown[];
  selectedActivities?: unknown[];
  selectionMode?: string;
  isEntireModuleSelected?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  params: undefined,
  selectedSections: () => [],
  selectedActivities: () => [],
  selectionMode: "",
  isEntireModuleSelected: false,
});

const emit = defineEmits<{
  (e: "updateSelectedSection", sectionId: unknown, isChecked: boolean): void;
  (e: "updateSelectedActivity", activityId: unknown, isChecked: boolean): void;
  (e: "selectEntireModule", isSelected: boolean): void;
  (e: "sectionToggle", value: { sectionId: unknown; expanded: boolean }): void;
}>();

const { t } = useI18n();
const vuexStore = useStore();

const store = ref(coursePlanStore);
const moduleToggle = ref(false);
const sectionToggle = ref(false);
const activityToggle = ref(false);
const toggleAccordionState = ref(false);
const sectionToggleState = ref<Record<string, boolean>>({});
const activitiesToggleState = ref<Record<string, boolean>>({});
const ENTIRE_MODULE_SELECTION_VALUE = "entire-module";

const isTeacher = computed(() => vuexStore.getters[`${NAMESPACE.AUTH}/isTeacher`]);
const getIntegratorAppAccessByAppFeatureId = computed(
  () => vuexStore.getters[`${NAMESPACE.APP}/getIntegratorAppAccessByAppFeatureId`],
);
const getPackageCompletionStatus = computed(
  () => vuexStore.getters[`${NAMESPACE.ATTEMPT}/getPackageCompletionStatus`],
);

const emptyText = computed<string>(() => "");

const module = computed(() => props.params?.moduleData);

const moduleId = computed<number>(() => parseInt(module.value.id));

const sequentialSectionCompletionReq = computed<boolean>(
  () => module.value.sequentialSectionCompletionReq,
);

const showSectionCover = computed<boolean>(() => module.value.showSectionCover);

const hideSectionActivityNumber = computed<boolean>(() => module.value.hideSectionActivityNumber);

const sections = computed<unknown[]>(() => module.value.sectionData);

const isMultipleSectionSelectionMode = computed(
  () =>
    props.params?.selectionMode === LessonSelectionModeEnum.multiple &&
    props.params.selectionType === LessonSelectionTypeEnum.sectionOrLesson &&
    showSectionCover.value,
);

const entireModuleSelectionValues = computed(() =>
  props.isEntireModuleSelected ? [ENTIRE_MODULE_SELECTION_VALUE] : [],
);

const notes = computed<string>(() => module.value.notes || t("module_plan_no_notes"));

const description = computed<string>(
  () => module.value.description || t("module_plan_no_description"),
);

const matchDescriptionOrNotes = computed<boolean>(
  () =>
    !isEmpty(props.params?.highlights?.description) || !isEmpty(props.params?.highlights?.notes),
);

const totalStudent = computed<number>(() =>
  module.value.overallProgress ? module.value.overallProgress.numStudentTotal : 0,
);

const sectionCompletionInfo = computed(() => (section: unknown) => section.progress);

const sectionCompletionInfoValues = computed(
  () =>
    (section: unknown): StackedColumnBarCategory[] => {
      // If a category quantity is 0, it will not be considered as a category.
      const completionInfo = sectionCompletionInfo.value(section);
      const totalComplete = completionInfo.numStudentCompleted;
      const totalInComplete = totalStudent.value - totalComplete;

      const completionStatusCategories = [] as StackedColumnBarCategory[];
      if (totalComplete > 0) {
        completionStatusCategories.push({
          name: t("completed_label"),
          cssClass: "completed",
          quantity: totalComplete,
        });
      }
      if (totalInComplete > 0) {
        const isOverDue = isSectionOverdue(section);
        completionStatusCategories.push({
          name: isOverDue ? t("overdue_label") : t("in_progress_label"),
          cssClass: isOverDue ? "overdue" : "in-progress",
          quantity: totalInComplete,
        });
      }

      return completionStatusCategories;
    },
);

const isAlsLesson = computed<boolean>(() => module.value.alsLesson);

const isExpanded = computed(() => (id: unknown) => store.value.isExpanded(id));

const isLoading = computed(() => (id: unknown) => store.value.isLoading(id));

const coverIcon = computed<string>(() =>
  module.value.showCover
    ? LESSON_ACTIVITY_INDICATOR_ICON.INTRODUCTION.VIEWABLE
    : LESSON_ACTIVITY_INDICATOR_ICON.INTRODUCTION.HIDDEN,
);

const moduleEndIcon = computed<string>(() => {
  if (!module.value.showCover) {
    return LESSON_ACTIVITY_INDICATOR_ICON.END.HIDDEN;
  }
  return getPackageCompletionStatus.value === COMPLETION_STATUS.COMPLETED
    ? LESSON_ACTIVITY_INDICATOR_ICON.END.COMPLETED
    : LESSON_ACTIVITY_INDICATOR_ICON.END.VIEWABLE;
});

const sectionIconType = computed(() => {
  const ICON = LESSON_ACTIVITY_INDICATOR_ICON.SECTION_END;
  return (section: unknown) => {
    if (!module.value.showSectionCover) {
      return ICON.HIDDEN;
    }
    if (isRestricted.value(section)) {
      return ICON.DISABLED;
    }
    if (section.statusInfo?.completed) {
      return ICON.COMPLETED;
    }
    return ICON.VIEWABLE;
  };
});

const sectionEndId = computed(() => (id: unknown) => `section-end-${id}`);

const showPrerequisite = computed(() => (section: unknown) => {
  const prerequisite = section.sectionPrerequisite;
  return prerequisite ? prerequisite.length !== 0 : false;
});

const isRestricted = computed(() => (section: unknown) => {
  if (showPrerequisite.value(section)) {
    const access = section.access;
    if (isNull(access)) {
      return false;
    }
    return (
      access.accessible === false &&
      access.failedReasons.includes(FAILED_REASON.PREREQUISITE_ACCESS_DENIED)
    );
  }
  return false;
});

const isBlank = computed(() => {
  const noDescription = description.value === t("module_plan_no_description");

  const noNotes = notes.value === t("module_plan_no_notes");

  return noDescription && (!isTeacher.value || noNotes);
});

const hasActivities = computed<boolean>(() => {
  let hasActivities = false;
  module.value.sectionData.every((section: unknown) => {
    if (section.activityData.length > 0) {
      hasActivities = true;
      return false;
    }
    return true;
  });
  return hasActivities;
});

const isSectionOverdue = (section: unknown) => {
  const endDateTime = section?.settings?.endDateTime;
  if (!endDateTime) return false;

  const dateTime = DateTimeFormatter.formatLocalDateTimeToDateTime(endDateTime);
  return DateTimeUtils.isNowAfterDate(dateTime);
};

const prerequisitesDropDownData = computed(() => {
  const dropDownData: { sections: unknown[] } = {
    sections: [],
  };
  sections.value.forEach((section: unknown) => {
    const activityList: unknown[] = [];
    section.activityData.forEach((activity: unknown) => {
      activityList.push({
        id: activity.id,
        title: activity.title,
        type: activity.type,
        pageNo: activity.pageNo,
      });
    });

    dropDownData.sections.push({
      id: section.id,
      title: section.title,
      pageNo: section.pageNo,
      activityList: activityList,
    });
  });
  return dropDownData;
});

const isAdaptiveSection = computed(() => (appFeatureIds: number[]) => {
  if (isEmpty(appFeatureIds)) return false;
  return appFeatureIds.some((id) => {
    const appFeature = getIntegratorAppAccessByAppFeatureId.value(id);
    return (
      !isNull(appFeature) &&
      appFeature.appFeatureName === INTEGRATOR_APP_FEATURE_CATEGORY.ALS &&
      appFeature.provider === INTEGRATOR_APP_PROVIDER.ADAPTEMY
    );
  });
});

const sectionToggleLabel = computed(
  () => (section: unknown) =>
    `${store.value.sectionToggleLabel(section.id)} (${section.activityData.length})`,
);

const subjectKey = computed(() => {
  if (module.value.metaData && module.value.metaData.subjectKey) {
    return module.value.metaData.subjectKey;
  }
  return null;
});

const cssVars = computed(() => ({
  // top border of section is core/ui-04 before it is assigned. Upon assigning, top border takes the colour of the class group tag.
  "--color": subjectKey.value ? subjectColorMap.getSubjectColor(subjectKey.value) : "#ced3d9",
}));

const moduleDetailActionsClass = computed(() => ({
  "resources-actions-wrapper": props.params?.type === "LESSON",
}));

function handleModuleAccordionToggle(event: AccordionActionData) {
  moduleToggle.value = event.state[0];
  updateToggle();
}

function handleSectionAccordionToggle({
  sectionId,
  expanded,
}: {
  sectionId: unknown;
  expanded: boolean;
}) {
  sectionToggleState.value[sectionId] = expanded;
  sectionToggle.value = sections.value.every(
    (section: unknown) => sectionToggleState.value[section.id],
  );
  updateToggle();
}

function handleActivityAccordionToggle(value: {
  data: { activityId: unknown; expanded: boolean; section: unknown };
}) {
  const { activityId, expanded, section } = value.data;

  // Initialize toggle state if not already present
  if (!Object.prototype.hasOwnProperty.call(activitiesToggleState.value, activityId)) {
    activitiesToggleState.value[activityId] = false;
  }

  // Update the specific activity's toggle state
  activitiesToggleState.value[activityId] = expanded;

  checkAllAccordionsToggled(section);
}

function checkAllAccordionsToggled(section: unknown) {
  // Check if every activity's id in section.activityData is present in expandedActivities
  // Set activityToggle based on whether all activities are expanded
  activityToggle.value = section.activityData.every(
    (activity: unknown) => activitiesToggleState.value[activity.id],
  );
  updateToggle();
}

function setExpanded(id: unknown, expanded: boolean) {
  store.value.setExpanded(id, expanded);
  emit("sectionToggle", {
    sectionId: id,
    expanded: expanded,
  });
}

function handleToggleExpand(id: unknown) {
  if (isExpanded.value(id)) {
    setExpanded(id, false);
  } else {
    setExpanded(id, true);
  }
}

function allActivities() {
  return sections.value.reduce((activities: unknown[], section: unknown) => {
    return activities.concat(section.activityData);
  }, []);
}

function handleExpandSectionIfActivityMatch() {
  const activities = allActivities();
  if (activities.length > 0) {
    activities.forEach((activity) => {
      const isHighlighted = props.params?.highlights?.activities?.some(
        (highlightedActivity: unknown) => highlightedActivity.id === activity.id,
      );
      // If the activity is highlighted, expand its section
      if (isHighlighted) {
        setExpanded(activity.parentId, true);
      }
    });
  }
}

function initializeSectionToggleStates() {
  const initState: Record<string, boolean> = {};
  sections.value.forEach((section: unknown) => {
    initState[section.id] = false;
  });
  sectionToggleState.value = initState;
}

function initializeActivityToggleStates() {
  // Set all activity states to false
  const activities = allActivities();

  if (activities.length > 0) {
    activities.forEach((activity) => {
      activitiesToggleState.value[activity.id] = false;
    });
  }
}

function handleAllAccordions(checked: boolean) {
  toggleAccordionState.value = checked;
  moduleToggle.value = checked;
  sectionToggle.value = checked;
  activityToggle.value = checked;
  const activities = allActivities(); // Get all activities using the allActivities method

  Object.keys(sectionToggleState.value).forEach((id) => {
    sectionToggleState.value[id] = checked;
  });

  if (activities.length > 0) {
    activities.forEach((activity) => {
      if (!Object.prototype.hasOwnProperty.call(activitiesToggleState.value, activity.id)) {
        activitiesToggleState.value[activity.id] = false;
      }
      activitiesToggleState.value[activity.id] = checked;
    });
  }
}

function updateToggle() {
  toggleAccordionState.value = moduleToggle.value && sectionToggle.value && activityToggle.value;
}

function updateSelectedSection(sectionId: unknown, isChecked: boolean) {
  emit("updateSelectedSection", sectionId, isChecked);
}

function handleSelectEntireModule(selectedValues: string[]) {
  emit("selectEntireModule", selectedValues.includes(ENTIRE_MODULE_SELECTION_VALUE));
}

function updateSelectedActivity(activityId: unknown, isChecked: boolean) {
  emit("updateSelectedActivity", activityId, isChecked);
}

// Lifecycle hook replacement
store.value.initStructureState(module.value, [], {});
initializeSectionToggleStates();
initializeActivityToggleStates();
handleExpandSectionIfActivityMatch();
</script>

<style lang="scss">
@import "@styles/abstracts/mixins";
@import "@styles/layout/breakpoint";
@import "@styles/layout/z-map";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.search-module-detail-plan {
  counter-reset: sectionCounter;

  > .wrapper {
    > .module-detail-actions {
      > .toggle-wrapper {
        margin: $spacing-05 0;

        .bx--toggle-input__label {
          display: flex;
          flex-flow: row-reverse;
          align-items: center;
          margin-left: auto;
          gap: $spacing-03;

          .bx--toggle__switch {
            margin-top: 0;
          }
        }
      }
    }
  }

  .wrapper {
    .module-detail-actions {
      // to align actions icons in a row for resources attachment
      &.resources-actions-wrapper {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        flex-wrap: wrap;
        gap: 0 $spacing-03;

        .module-detail-header {
          flex: 1 1 auto;
        }
      }
    }

    .module-detail-content {
      .module-plan-activity-component {
        &:first-child {
          .page-component {
            border-top-left-radius: 10px;
            border-top-right-radius: 10px;
          }
        }

        &:last-child {
          .page-component {
            border-bottom-right-radius: 10px;
            border-bottom-left-radius: 10px;
          }
        }
      }

      .match-indicator {
        margin-bottom: $spacing-05;
      }
    }

    .module-plan-activity-component-list {
      &:has(.bx--accordion) {
        border-radius: 10px;
      }

      .module-plan-activity-component {
        &:not(:last-child) {
          .bx--accordion__item {
            margin-bottom: 0;
            border-bottom: $divider-02;
            border-radius: 0;
          }

          .bx--accordion__content {
            border-radius: 0;
          }
        }

        &:first-child {
          .bx--accordion__item {
            border-top-left-radius: 10px;
            border-top-right-radius: 10px;
          }
        }

        .bx--accordion__heading {
          padding: 14px 0; // to keep intro accordion bar at height 60px
        }

        .match-indicator {
          margin-bottom: $spacing-03;
        }
      }

      .section-end {
        &:last-child {
          border-bottom-left-radius: 10px;
          border-bottom-right-radius: 10px;
        }

        &:only-child {
          border-radius: 10px;
        }
      }
    }

    .activities-detail-content {
      counter-reset: activityCounter;

      .module-plan-activity-component {
        &.activity {
          counter-increment: activityCounter;
        }
      }

      .activity {
        .component-content {
          .field-set.title {
            .field-value {
              .icon:before {
                content: counter(activityCounter);
              }
            }
          }
        }

        .accordion-title {
          .title {
            .icon {
              &::before {
                content: counter(activityCounter);
              }
            }
          }
        }
      }

      &:has(.bx--accordion) {
        margin-bottom: $spacing-03;
        border-radius: 10px;
      }
    }

    .bx--form {
      width: 100%;
      margin: 0;

      .field-set {
        margin-bottom: 0;
      }
    }

    .module-plan-accordion-component {
      &.detail {
        margin-bottom: $spacing-05;

        .bx--accordion__heading {
          padding: 14px 0; // to keep intro accordion bar at height 60px
        }
      }

      .accordion-title {
        @include carbon--type-style("panel-title-01");
        display: flex;
        align-items: center;
        gap: $spacing-03;
        margin-right: $spacing-05;
        color: $text-02;

        .icon {
          max-height: 32px;

          &.hide-section-activity-number {
            &::before {
              display: none;
            }
          }
        }

        .sls-icon {
          &[name*="SectionHidden"] {
            > [stroke] {
              stroke: $ui-01;
            }
          }
        }
      }

      .schedule {
        width: 100%;
        display: flex;
        align-items: center;
        align-self: center;
        justify-content: center;
        margin-right: $spacing-05;
        padding: $spacing-03;
        gap: $spacing-05;
        background-color: $ui-background;
        border-radius: 10px;

        @include media("tablet-min") {
          width: 20rem;
          margin-left: auto;
        }

        @include media("desktop") {
          background-color: $ui-01;
        }

        .start,
        .end {
          display: flex;
          flex-flow: column;
          align-items: center;
          text-align: center;
        }

        .date {
          @include carbon--type-style("productive-heading-01");
          color: $text-02;
        }

        .time {
          @include carbon--type-style("label-03");
          color: $text-03;
        }

        .field-value {
          margin-top: 0;
          gap: $spacing-03;
        }
      }

      .status {
        width: 100%;

        > .field-value {
          .label-component {
            margin-right: $spacing-05;
          }
        }

        @include media("tablet-min") {
          width: auto;
          > .field-value {
            .label-component {
              margin-right: 0;
            }
          }
        }

        @include media("desktop") {
          margin-left: auto;
        }
      }

      .action-buttons {
        position: absolute;
        right: 2.5rem;
        top: 3px;
        display: flex;
        align-items: center;
      }

      .bx--accordion {
        &__item {
          &--active {
            .bx--accordion__heading {
              .bx--accordion__title {
                &::after {
                  top: 13px;
                  border-color: $interactive-01;
                }
              }

              .sls-icon {
                &:not([name="Match16"]) {
                  [stroke] {
                    stroke: $interactive-01;
                  }
                }

                &[name*="SectionHidden"] {
                  > [stroke] {
                    stroke: $ui-01;
                  }
                }
              }
            }
          }
        }

        &__heading {
          background-color: $ui-01;

          .bx--accordion__title {
            .field-value {
              display: flex;
              align-items: center;

              .icon-video {
                display: inline-flex;
              }

              .activity-prerequisite {
                width: 100%;

                .sls-icon {
                  path {
                    &:last-of-type {
                      stroke: $ui-01;
                    }
                  }
                }
              }
            }

            &::after {
              top: 8px;
              @include media("tablet-min") {
                top: 10px;
              }
            }
          }

          .section-label {
            position: relative;

            &::before {
              content: attr(data-section-icon-text);
              @include carbon--type-style("helper-text-01", true);
              width: 100%;
              position: absolute;
              top: 0.7rem;
              color: $ui-01;
              text-align: center;
            }
          }
        }

        &__title {
          padding-right: 0;
          z-index: unset; // to prevent prerequisties popup overlay by title

          .component-content {
            width: 100%;
            display: flex;
            flex-flow: row wrap;
            row-gap: $spacing-05;

            @include media("tablet-min") {
              padding-right: $spacing-09;
              align-items: center;
            }

            @include media("desktop") {
              padding-right: $spacing-11;
              flex-wrap: nowrap;
            }

            .meta-info {
              display: inline-flex;

              .differentiated-access {
                display: inline-flex;

                .bx--tooltip__trigger {
                  margin: 0 $spacing-05;
                }
              }
            }
          }

          .wrapper {
            width: 100%;
          }

          .locator {
            position: absolute;
            left: -2rem;
            top: 50%;
            transform: translateY(-50%);

            .sls-icon {
              [fill] {
                fill: $support-03;
              }
            }
          }

          .input-radio {
            &.disabled {
              .sls-icon {
                > [stroke] {
                  stroke: transparent;
                }
              }
            }
          }
        }

        &__content {
          padding-bottom: $spacing-05;

          .wrapper {
            display: flex;
            flex-flow: column;
            gap: $spacing-03;

            h6 {
              @include carbon--type-style("card-title-01");
              color: $text-02;
            }

            .rich-text {
              @include carbon--type-style("body-short-01", true);
            }
          }

          .recommended-time {
            display: flex;
            align-items: center;
            gap: $spacing-03;
            margin-bottom: $spacing-05;

            time {
              @include carbon--type-style("body-short-01");
              color: $text-01;
            }
          }
        }
      }

      .match-indicator {
        .sls-icon {
          [stroke] {
            stroke: $support-02;
          }
        }
      }

      .toggle-wrapper {
        position: absolute;
        right: 3rem;
        top: 14px;

        .bx--toggle__switch {
          margin-top: 0;
        }
      }

      .activity-prerequisite {
        display: flex;
        align-items: center;
        gap: $spacing-03;
        margin-left: $spacing-02;
      }
    }

    .module-detail-content {
      :is(.section-detail-content, .activities-detail-content) {
        .bx--form-item {
          align-items: unset;
        }

        .accordion-title {
          .icon {
            position: relative;

            &::before {
              @include carbon--type-style("helper-text-01");
              @include position-mixin($left: 50%, $bottom: 0.45rem);
              content: counter(activityCounter);
              transform: translateX(-50%);
              color: $icon-01;
            }
          }
        }
      }

      .viewable-toggle {
        // Absolute position and padding to enlarge the click zone of the toggle while maintaing
        // its position
        position: absolute;
        display: flex;
        align-items: center;
        padding: $spacing-07 $spacing-05;
        right: 3.5rem;
        width: fit-content;

        // Text for toggle will be hidden in Mobile view due to space constraints
        @include media("mobile") {
          .label {
            display: none;
          }
        }

        &.disabled {
          .viewable-info {
            .label {
              color: $disabled-01;
            }
          }

          .bx--toggle-input__label {
            > .bx--toggle__switch {
              &::before {
                opacity: 0.5;
              }
            }
          }
        }

        &.section {
          right: 5rem;

          @include media("mobile") {
            right: 4rem;
          }
        }

        .viewable-info {
          .label {
            @include carbon--type-style("body-short-01");
            margin-right: $spacing-03;
            color: $text-02;
            white-space: nowrap;
          }
        }

        .bx--form-item {
          label {
            .bx--toggle__switch {
              margin-top: 0;

              &::after {
                background-color: $ui-01;
              }
            }
          }
        }
      }

      .activities-detail-content {
        .page-component {
          margin-bottom: $spacing-05;
          border-radius: 10px;
        }

        .introduction {
          .bx--accordion__heading {
            background-color: $ui-background;

            .field-value {
              margin-left: $spacing-03;
            }
          }

          .bx--accordion__content {
            background-color: $ui-background;

            > h6 {
              color: $text-02;
            }

            .rich-text {
              margin: $spacing-03 0;
            }
          }
        }

        :is(.bx--accordion__heading, .bx--accordion__content) {
          background-color: $ui-background;
        }

        .empty-resource-plan {
          @include carbon--type-style("productive-heading-02");
          display: flex;
          flex-flow: column;
          align-items: center;
          margin-top: $spacing-07;
          color: $disabled-02;

          svg {
            margin-bottom: $spacing-05;
          }
        }
      }

      .introduction {
        .bx--accordion__item--active {
          .bx--accordion__heading {
            border-radius: 10px 10px 0 0;
          }
        }

        .bx--accordion__heading {
          background-color: $ui-background;
          border-radius: 10px;
        }

        .bx--accordion__content {
          background-color: $ui-background;

          .rich-text-content {
            .attachment-placeholder {
              background-color: $ui-01;
            }
          }
        }
      }

      .page-component {
        display: flex;
        align-items: center;
        padding: 14px 1.75rem;
        position: relative;
        background-color: $ui-background;

        &.section {
          padding: 14px $spacing-05;

          > .wrapper {
            > .component-content {
              > .title {
                max-width: calc(100% - 6rem);
              }
            }
          }
        }

        &.introduction,
        &.module-end {
          padding: 14px 1.75rem;
          background-color: $ui-background;
          border-radius: 10px;
        }

        &.introduction {
          margin-bottom: $spacing-05;
        }

        &.section {
          background-color: $ui-01;
          border-radius: 10px;
          counter-increment: sectionCounter;

          .action-buttons {
            .section-cover-viewable-toggle ~ .bx--btn {
              margin-left: $spacing-04;
            }

            @include media("mobile") {
              position: absolute;
              top: 0.75rem;
              right: 0.25rem;
            }
          }

          .schedule {
            @include media("mobile") {
              width: 100%;
              margin: $spacing-05 0;
              background-color: $ui-background;
              border-radius: 10px;
            }

            @include media("tablet-min") {
              flex: 0 0 15rem;
              margin: 0 $spacing-05;
            }

            .field-value {
              display: flex;
              justify-content: space-around;
              text-align: center;

              .date {
                color: $text-02;
                text-transform: uppercase;
              }

              .time {
                color: $text-03;
              }
            }
          }

          .status {
            flex: 0 0 6.5rem;
          }

          .component-content {
            .meta-info {
              min-width: 15rem;
              margin: 0 $spacing-05;
            }

            @include media("mobile") {
              flex-flow: row wrap;

              .title {
                max-width: calc(100% - 4.5rem);

                // For the text input in edit mode not to overlap with the action buttons
                .field-input {
                  max-width: calc(100% - 2rem);
                }
              }

              .meta-info {
                width: 100%;
                padding: $spacing-03;
                margin: $spacing-05 auto;
                background-color: $ui-background;
                border-radius: 10px;
              }
            }
          }

          .component-content {
            .field-set.title {
              .field-value {
                .icon {
                  &.hide-section-activity-number {
                    &:not(.editor-mode) {
                      .section-label {
                        &::before {
                          opacity: 0;
                        }
                      }
                    }

                    &.editor-mode {
                      .section-label {
                        &::before {
                          opacity: 0.5;
                        }
                      }
                    }
                  }
                }

                .section-label {
                  &::before {
                    @include carbon--type-style("helper-text-01");
                    content: attr(data-section-icon-text);
                    position: absolute;
                    top: 50%;
                    width: 100%;
                    transform: translateY(-50%);
                    color: $ui-01;
                    text-align: center;
                  }
                }
              }
            }
          }
        }

        .action-buttons {
          display: flex;
          align-items: center;
          align-self: flex-end;
          flex: 0 0 auto;
          margin-left: auto;
          padding-left: 1rem;

          .fetch-data-button {
            margin-right: $spacing-03;
          }

          .bx--overflow-menu {
            &:hover,
            &:active {
              background-color: $ui-01;
            }
          }

          .bx--tooltip__trigger {
            svg {
              fill: none;
            }
          }

          .icon-button {
            width: 40px;
            height: 40px;
            padding: 0;
          }
        }

        &.disabled {
          cursor: not-allowed;
          pointer-events: none;

          .field-set {
            .field-value {
              color: $text-03;

              .icon:before {
                color: $text-03;
              }
            }
          }
        }

        &.edit {
          cursor: default;

          &:hover {
            background-color: $ui-01;
          }
        }

        > .wrapper {
          display: flex;
          align-items: center;
          width: 100%;

          .component-content {
            display: flex;
            flex: 1 1 0%;
            min-width: 0;
            align-items: center;

            .activity-toggle {
              flex: 0 0 $spacing-07;

              .bx--toggle__switch {
                margin-top: 0;
              }
            }

            .text {
              @include line-clamp(2);
              word-break: break-word;
              margin-right: $spacing-03;
            }

            .pending-release {
              min-width: 16px;
              margin: 0 $spacing-03;
            }
          }
        }

        .locator {
          display: inline-flex;
          position: absolute;
          left: -1rem;
          top: 50%;
          transform: translateY(-50%);

          .sls-icon {
            [fill] {
              fill: $support-03;
            }
          }
        }

        .field-set {
          display: flex;
          align-items: center;
          flex-flow: row wrap;
          flex: 1 1 auto;

          &.schedule {
            .field-value {
              display: flex;
              justify-content: space-around;
              text-align: center;
            }
          }

          .field-label,
          .field-input {
            flex: 0 0 100%;
            max-width: 100%;
          }

          .field-input {
            .bx--text-input {
              background-color: $ui-background;
            }
          }

          .field-value {
            @include carbon--type-style("panel-title-01", true);
            display: inline-flex;
            align-items: center;
            width: 100%;
            color: $text-02;

            .icon {
              display: flex;
              justify-content: center;
              align-items: center;
              position: relative;

              &::before {
                @include carbon--type-style("helper-text-01");
                position: absolute;
                bottom: 0.45rem;
                color: $icon-01;
              }

              &.hide-section-activity-number {
                &:not(.editor-mode) {
                  &::before {
                    opacity: 0;
                  }
                }

                &.editor-mode {
                  &::before {
                    opacity: 0.5;
                  }
                }
              }
            }

            .icon-video {
              display: inline-flex;

              .sls-icon {
                [stroke] {
                  stroke: $icon-01;
                }
              }
            }
          }
        }
      }

      // Sections
      .section-grouper {
        position: relative;
        padding: $spacing-05;
        margin-bottom: $spacing-05;
        width: 100%;
        background-color: $ui-background;
        border: 1px solid $ui-04;
        border-radius: 10px;

        &:before {
          content: "";
          position: absolute;
          top: 0;
          left: 2px;
          width: calc(100% - 4px);
          border-top: 4px solid var(--color);
          z-index: calc(#{z-map("default")} + 2);
          border-radius: 10px 10px 0 0;
        }

        .page-component {
          .locator {
            left: -2rem;
          }
        }
      }

      .section-info {
        display: flex;
        flex-flow: row wrap;
        align-items: center;
        gap: $spacing-05;
        padding-top: $spacing-05;
        gap: $spacing-05;

        .section-progress {
          flex: 0 0 100%;
        }

        .actions-buttons {
          @include carbon--type-style("card-short-01", true);
          display: flex;
          flex-flow: row wrap;
          align-items: center;
          flex: 0 0 100%;

          > .section-prerequisite {
            flex: 0 0 100%;
            align-items: center;
            margin-bottom: $spacing-05;
          }

          > .label {
            @include carbon--type-style("body-short-01");
            color: $text-02;
          }

          .bx--link {
            text-decoration: none;

            &:focus {
              outline: none;
            }
          }

          .section-toggle-access {
            display: flex;
            align-items: center;
            margin-left: auto;

            .access-count {
              @include carbon--type-style("label-03", true);
              padding: $spacing-02 $spacing-03;
              margin: 0 $spacing-04;
              background-color: $highlight;
              border-radius: 5px;
              color: $state-neutral;
            }

            .access-info {
              display: flex;
              align-items: center;

              .label {
                @include carbon--type-style("label-03", true);
                color: $text-02;
              }
            }

            .bx--toggle__switch {
              margin-top: 0;
            }
          }
        }
      }

      .section-activities {
        margin-top: $spacing-05;
        padding-top: $spacing-05;
        border-top: $divider-02;
        counter-reset: activityCounter;

        > .wrapper {
          background-color: $ui-01;
          border-radius: 10px;
        }

        .bx--accordion {
          &.accordion-activity {
            counter-increment: activityCounter;
          }
        }

        .page-component {
          background-color: $ui-01;
          border-bottom: 1px solid $ui-02;
          padding: 14px $spacing-05;

          &:hover {
            background-color: $hover-ui;
          }

          &.activity {
            counter-increment: activityCounter;

            .component-content {
              .field-set.title {
                .field-value {
                  flex-flow: row wrap;

                  .activity-prerequisite {
                    display: flex;
                    flex: 0 0 100%;
                    align-items: center;
                    padding-left: $spacing-02;

                    > .sls-icon {
                      margin-right: $spacing-03;
                    }
                  }

                  .icon:before {
                    content: counter(activityCounter);
                  }

                  .text {
                    // To cater for the page icon and pending-release icon
                    max-width: calc(100% - 5rem);
                  }
                }
              }

              .activity-toggle {
                flex: 0 0 2rem;
                margin-left: $spacing-03;

                .bx--toggle__switch {
                  margin-top: 0;
                }
              }

              .meta-info {
                display: flex;
                align-items: center;

                > div {
                  &:not(:first-of-type) {
                    margin-left: $spacing-03;
                  }
                }

                .field-tooltip {
                  display: flex;
                  align-items: center;

                  .bx--tooltip__trigger {
                    margin: 0 $spacing-03;
                  }
                }
              }
            }
          }
        }
      }

      .field-value {
        column-gap: $spacing-03;
      }
    }

    .module-actions {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: $spacing-05 0;
    }

    .module-plan-section-component {
      .bx--accordion__item {
        .bx--accordion__heading {
          display: flex;
          align-items: center;

          .sls-icon {
            [stroke] {
              stroke: $ui-04;
            }
          }

          .wrapper {
            min-height: 3.2rem;
            display: flex;
          }

          .bx--accordion__title {
            &::after {
              top: 17px;
            }
          }
        }

        &--active {
          .bx--accordion__heading {
            .bx--accordion__title {
              &::after {
                top: 23px;
              }
            }
          }
        }
      }
    }
  }
}
</style>
